import axios from 'axios';
import { getApiUrl } from '../../../config/api';

// Only OWNER-shaped keys are used for scoping. admin_email and
// recruiter_email are intentionally NOT here — admin is a separate
// super-user role, recruiter is a third-party vendor. Using either as a
// scope key caused the cross-session contamination bug where saved
// interviewers ended up under an admin's email instead of the company
// owner's email. Matches the backend's utils/company_scope.py resolver.
const OWNER_KEYS = [
  'employer_email',
  'company_email',
];

const readStored = (key) => {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key) || '';
};

const getCompanyEmail = () => {
  if (typeof window === 'undefined') return '';
  for (const key of OWNER_KEYS) {
    const value = readStored(key);
    if (value) return value.trim().toLowerCase();
  }
  return '';
};

/**
 * List the saved interviewers for the current company. The backend reads
 * the company identity from the session; we also pass it as a query param
 * fallback so the call works when session cookies are not available (eg.
 * staging vs. localhost crossing origins).
 */
export async function listSavedInterviewers() {
  const params = {};
  const company = getCompanyEmail();
  if (company) params.company_email = company;
  const response = await axios.get(getApiUrl('/api/company/saved-interviewers'), {
    params,
    withCredentials: true,
  });
  return Array.isArray(response.data?.interviewers) ? response.data.interviewers : [];
}

/**
 * Add or update a saved interviewer for the current company.
 * Returns the persisted row from the backend.
 */
export async function upsertSavedInterviewer({
  interviewerEmail,
  interviewerName = '',
  jobTitle = '',
} = {}) {
  const normalized = String(interviewerEmail || '').trim().toLowerCase();
  if (!normalized) {
    throw new Error('Interviewer email is required.');
  }
  const company = getCompanyEmail();
  const response = await axios.post(
    getApiUrl('/api/company/saved-interviewers'),
    {
      company_email: company || undefined,
      actor_email: company || undefined,
      interviewer_email: normalized,
      interviewer_name: interviewerName || '',
      job_title: jobTitle || '',
    },
    { withCredentials: true }
  );
  return response.data?.interviewer;
}

/**
 * Remove a saved interviewer for the current company.
 * Returns true if the row was deleted.
 */
export async function deleteSavedInterviewer({ interviewerEmail } = {}) {
  const normalized = String(interviewerEmail || '').trim().toLowerCase();
  if (!normalized) {
    throw new Error('Interviewer email is required.');
  }
  const company = getCompanyEmail();
  const response = await axios.delete(getApiUrl('/api/company/saved-interviewers'), {
    data: {
      company_email: company || undefined,
      interviewer_email: normalized,
    },
    params: company ? { company_email: company } : undefined,
    withCredentials: true,
  });
  return Boolean(response.data?.deleted);
}
